// Keyless geocoding. Primary: OpenStreetMap Nominatim (matches full street addresses AND
// cities), falling back to the Open-Meteo Geocoding API (fast city ranking) when Nominatim
// returns nothing. Both are CORS-enabled and keyless.

const NOMINATIM_URL = "https://nominatim.openstreetmap.org/search";
const SEARCH_URL = "https://geocoding-api.open-meteo.com/v1/search";

const REGION_ALIASES = {
  bc: "british columbia", ab: "alberta", on: "ontario", qc: "quebec", mb: "manitoba",
  sk: "saskatchewan", ns: "nova scotia", nb: "new brunswick", nl: "newfoundland and labrador",
  pei: "prince edward island", pe: "prince edward island", yt: "yukon",
  nt: "northwest territories", nu: "nunavut",
  ny: "new york", tx: "texas", fl: "florida", wa: "washington", il: "illinois",
  ma: "massachusetts", pa: "pennsylvania", az: "arizona", co: "colorado", ga: "georgia",
  uk: "united kingdom", usa: "united states", us: "united states", uae: "united arab emirates",
};

// Concise label from a Nominatim address record: "house road, city, country".
function shortAddr(r) {
  const a = r.address || {};
  const line1 = [a.house_number, a.road].filter(Boolean).join(" ");
  const city = a.city || a.town || a.village || a.suburb || a.county || a.state || "";
  const bits = [line1, city, a.country].filter(Boolean);
  if (bits.length) return [...new Set(bits)].join(", ");
  const parts = (r.display_name || "").split(",").map((s) => s.trim());
  return parts.length <= 3 ? r.display_name : `${parts[0]}, ${parts[parts.length - 1]}`;
}

async function searchAddress(query) {
  const url = `${NOMINATIM_URL}?format=jsonv2&q=${encodeURIComponent(query)}&addressdetails=1&limit=6`;
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`nominatim ${res.status}`);
  const data = await res.json();
  return (data || [])
    .filter((r) => r.lat && r.lon)
    .map((r) => ({ name: shortAddr(r), lat: +r.lat, lng: +r.lon }));
}

async function searchCities(q) {
  const parts = q.split(",").map((s) => s.trim()).filter(Boolean);
  const city = parts[0];
  const cityLc = city.toLowerCase();
  const regionTokens = parts.slice(1).map((t) => t.toLowerCase());
  const expanded = regionTokens.map((t) => REGION_ALIASES[t] || t);
  const allTokens = [...new Set([...regionTokens, ...expanded])];

  const url = `${SEARCH_URL}?name=${encodeURIComponent(city)}&count=10&language=en&format=json`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`geocode ${res.status}`);
  const data = await res.json();

  const rows = (data.results || []).map((r) => ({
    raw: (r.name || "").toLowerCase(),
    name: [r.name, r.admin1, r.country].filter(Boolean).join(", "),
    admin1: (r.admin1 || "").toLowerCase(),
    country: (r.country || "").toLowerCase(),
    population: r.population || 0,
    featureCode: r.feature_code || "",
    lat: r.latitude,
    lng: r.longitude,
  }));

  const regionMatch = (row) =>
    allTokens.some((t) => (row.admin1 && (row.admin1.includes(t) || t.includes(row.admin1))) || (row.country && row.country.includes(t)));

  const score = (row) => {
    let s = 0;
    allTokens.forEach((t) => {
      if (row.admin1 && (row.admin1.includes(t) || t.includes(row.admin1))) s += 20;
      if (row.country && row.country.includes(t)) s += 10;
    });
    if (row.raw === cityLc) s += 15;
    if (row.featureCode.startsWith("PPL")) s += 8;
    s += Math.min(8, Math.log10((row.population || 0) + 1) * 1.2);
    return s;
  };

  let ranked = rows.map((r) => ({ r, s: score(r) })).sort((a, b) => b.s - a.s).map((x) => x.r);
  if (allTokens.length) {
    const matched = ranked.filter(regionMatch);
    if (matched.length) ranked = matched;
  }
  return ranked.slice(0, 6).map(({ name, lat, lng }) => ({ name, lat, lng }));
}

export async function searchPlaces(query) {
  const q = (query || "").trim();
  if (q.length < 2) return [];
  try {
    const addr = await searchAddress(q);
    if (addr.length) return addr.slice(0, 6);
  } catch {
    /* fall through to city search */
  }
  try {
    return await searchCities(q);
  } catch {
    return [];
  }
}
